import { ErrorBoundary } from "@appsignal/react";
import { createInertiaApp, router } from "@inertiajs/react";
import type { ComponentType } from "react";
import { createRoot } from "react-dom/client";
import { appsignal, initializeAppsignal } from "../appsignal";
import { installPreloadRecovery } from "../lib/preloadRecovery";
import type { PostHogRuntimeConfig } from "../lib/publicConfig";
import { type AnalyticsPage, initializeAnalytics, trackPage } from "../posthog";
import "../styles/application.css";

// Inertia 3's auto (CSR) setup options: `el` is `HTMLElement | null`.
// The type isn't exported from the package, so derive it from the entry.
type SetupOptions = Parameters<
  NonNullable<NonNullable<Parameters<typeof createInertiaApp>[0]>["setup"]>
>[0];

const defaultTitle = "Weft — API search and payments for AI agents";
const defaultDescription =
  "Weft helps AI agents find, buy, and use APIs and services without stopping for account setup, API keys, or subscriptions.";

// Lazily resolve page components — each page becomes its own dynamic chunk
// instead of being eagerly bundled into the entry chunk. Inertia awaits the
// returned promise and unwraps the module's default export.
const pages = import.meta.glob<{ default: ComponentType }>([
  "../pages/**/*.tsx",
  "!../pages/**/*.test.tsx",
  "!../pages/**/*.spec.tsx",
]);

const appElement = document.getElementById("app");

// Inertia 3 carries the initial page on a `<script type="application/json"
// data-page>` element (inertia_rails `use_script_element_for_initial_page`),
// so the mount point div itself no longer carries `data-page`.
const pageScriptEl = document.querySelector(
  'script[data-page="app"][type="application/json"]',
);
const hasInertiaPage = !!appElement && Boolean(pageScriptEl);

// The RUNTIME config (env + revision) is inside the initial page payload —
// parse it here, at module scope, so AppSignal can initialize BEFORE
// createInertiaApp resolves the initial page. A missing page, a rejected
// dynamic import, or a throw during resolution would otherwise happen before
// the window-events plugin / ErrorBoundary exist, silently losing the boot
// failure (the exact class of error AppSignal exists to catch).
//
// The payload also feeds `setup()` below; it is parsed once here because
// that path runs after page resolution.
const initialPayload = pageScriptEl
  ? (JSON.parse(pageScriptEl.textContent ?? "null") as {
      props?: { public_config?: { environment?: string; revision?: string } };
    } | null)
  : null;

if (hasInertiaPage) {
  const revision = initialPayload?.props?.public_config?.revision ?? "unknown";

  // Server-served runtime env + revision (NOT build-time VITE_ vars — the
  // same image is promoted staging→prod unmodified). Must run before
  // createInertiaApp so pre-render boot errors are captured.
  initializeAppsignal({
    environment:
      initialPayload?.props?.public_config?.environment ?? "development",
    revision,
  });
  installPreloadRecovery(revision);

  createInertiaApp({
    serverHead: (page) => {
      const url = new URL(page.url, window.location.origin).href;
      return [
        `<title data-inertia="">${defaultTitle}</title>`,
        `<meta data-inertia="description" name="description" content="${defaultDescription}">`,
        `<meta data-inertia="og:title" property="og:title" content="${defaultTitle}">`,
        `<meta data-inertia="og:description" property="og:description" content="${defaultDescription}">`,
        `<meta data-inertia="og:url" property="og:url" content="${url}">`,
        `<meta data-inertia="twitter:title" name="twitter:title" content="${defaultTitle}">`,
        `<meta data-inertia="twitter:description" name="twitter:description" content="${defaultDescription}">`,
      ];
    },
    resolve: (name) => {
      const page = pages[`../pages/${name}.tsx`];
      if (!page) {
        throw new Error(`Page not found: ${name}`);
      }
      return page().then((module) => module.default);
    },
    setup({ el, App, props }: SetupOptions) {
      if (!el) return;
      const initialPage = props.initialPage as AnalyticsPage;
      // `public_config` is set in ApplicationController. PostHog is the only
      // consumer left — the Crossmint browser SDK went away with the wallet
      // card, and with it the whole provider tree that used to wrap the app.
      const publicConfig = initialPage.props.public_config as
        | { posthog?: PostHogRuntimeConfig }
        | undefined;
      initializeAnalytics(
        publicConfig?.posthog,
        initialPage.props.analytics_identity,
      );

      // Safely extract component name for AppSignal action tracking
      const componentName = props.initialPage.component ?? "Unknown";

      const appTree = <App {...props} />;

      createRoot(el).render(
        appsignal ? (
          <ErrorBoundary
            instance={appsignal}
            action={componentName}
            fallback={(_error) => (
              <div className="p-8 text-center">
                <h1>Something went wrong</h1>
                <p>
                  The error has been reported. Please try refreshing the page.
                </p>
              </div>
            )}
          >
            {appTree}
          </ErrorBoundary>
        ) : (
          appTree
        ),
      );
    },
  });
}

// Inertia emits `navigate` for the initial visit as well as later SPA
// navigations, so this is the single pageview capture path.
router.on("navigate", (e) => {
  trackPage(e.detail.page as AnalyticsPage);
});
